import PropTypes from 'prop-types';
import { hasAgentScaffolding } from '../utils/agentScaffolding';

/**
 * The waiting state for an assistant turn: one line of status text, shimmering
 * on a small surface of its own.
 *
 * Deliberately not an avatar-plus-bubble with bouncing dots. But bare text on
 * the page ground reads as a stray line rather than as the app working, so it
 * sits on the same raised panel the activity timeline uses, with the same
 * turning ring — the waiting state and the trail it hands off to should look
 * like one object, not two features.
 */

// The backend prefixes some statuses with an emoji ("<sparkle> Generating
// insights..."). The shimmer sweep has to start on a word, so drop a leading
// run of pictographs (plus the variation selectors / joiners they carry).
// U+FE0F variation selector, U+200D zero-width joiner, U+20E3 keycap enclosure.
const LEADING_PICTOGRAPH_RE =
  /^(?:[\p{Extended_Pictographic}\p{So}\uFE0F\u200D\u20E3]\s*)+/u;

// The status is whatever prose the model is emitting, so it arrives carrying
// markdown. It is rendered as text (a shimmer needs one run of characters, not
// a document), which would otherwise put literal asterisks and backticks on
// screen — so the syntax is stripped rather than rendered.
const stripMarkdown = (text) => text
  .replace(/`{1,3}([^`]*)`{1,3}/g, '$1')
  .replace(/\*\*([^*]+)\*\*/g, '$1')
  .replace(/(^|\s)[*_]([^*_]+)[*_](?=\s|$)/g, '$1$2')
  .replace(/^#{1,6}\s+/, '')
  .replace(/^[-*+]\s+/, '');

/**
 * Reduce whatever the pipeline sent to a single line of words. Statuses arrive
 * as free text and sometimes carry newlines; this is a status line, not a
 * document, and the shimmer gradient only reads correctly on one run of text.
 *
 * Returns '' for agent machinery — the stream also carries raw scaffolding like
 * "[tool Retrieving CMS Data: ok]", which the activity timeline already renders
 * properly as a typed step. Showing it here too would be the internals twice.
 */
const cleanStatus = (raw) => {
  if (typeof raw !== 'string') return '';
  if (hasAgentScaffolding(raw)) return '';
  const oneLine = raw.replace(/\s+/g, ' ').trim();
  const withoutLeadingIcon = oneLine.replace(LEADING_PICTOGRAPH_RE, '').trim();
  const stripped = stripMarkdown(withoutLeadingIcon).trim();
  // A bare bracketed tag is scaffolding the detector doesn't cover.
  if (/^\[[^\]]*\]$/.test(stripped)) return '';
  return stripped;
};

const ChatTypingIndicator = ({ statusMessage }) => {
  // A status that was nothing but an emoji cleans down to empty — still needs a label.
  const label = cleanStatus(statusMessage) || 'Thinking...';

  return (
    <div className="w-full px-3 py-2 animate-rise">
      {/* Flush with the assistant column: px-3 here matches ChatMessage's p-3,
          so the answer that replaces this starts at the same edge and nothing
          shifts at handoff. The panel hugs its text rather than spanning the
          column — a full-width bar would read as an empty answer. */}
      <div
        role="status"
        className="inline-flex max-w-[min(42rem,100%)] items-center gap-2.5 rounded-xl border border-line bg-surface-raised py-2 pl-2.5 pr-3.5"
      >
        {/* Same turning ring as the timeline header, so the handoff from
            "working" to "here is what I did" is one continuous object. */}
        <span
          aria-hidden="true"
          className="orb-spin relative grid h-3.5 w-3.5 shrink-0 place-items-center rounded-full"
        >
          <span className="block h-2 w-2 rounded-full bg-surface-raised" />
        </span>
        {/* Statuses run from two words to a paragraph. One line, clipped —
            the full reasoning is in the timeline, not in the spinner. */}
        <span className="min-w-0 truncate text-sm font-medium shimmer-text">
          {label}
        </span>
      </div>
    </div>
  );
};

ChatTypingIndicator.propTypes = {
  statusMessage: PropTypes.string,
};

export default ChatTypingIndicator;
